import { useEffect } from "react";
import { useParams, useLocation } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  Calendar,
  Clock,
  MapPin,
  User,
  Armchair,
  Loader2,
  AlertCircle,
  CheckCircle2,
  XCircle,
  QrCode,
  Ticket,
  Phone,
  Building2,
  ChevronDown,
} from "lucide-react";
import { useTenant } from "@/contexts/TenantContext";
import { publicApi } from "@/lib/api";
import { trackPageView } from "@/lib/analytics";
import { SEO, SEOPresets } from "@/components/shared/SEO";
import { Button } from "@/components/ui/button";
import { formatInTimezone, DEFAULT_TIMEZONE } from "@shared/timezone-utils";
import { useDateLocale } from "@/lib/dateLocale";

interface TicketData {
  ticketNumber: string;
  status: string;
  // F2: an `open` parked travel credit is not a boarding pass. The server sends
  // isTravelCredit + the redemption deadline and suppresses the boarding QR.
  isTravelCredit?: boolean;
  openTicketExpiresAt?: string | null;
  /**
   * Unpaid booking (Apartado): held, NOT confirmed. Derived server-side (raw
   * paymentStatus is never sent). Older responses omit it: no badge.
   */
  awaitingPayment?: boolean;
  passengerName: string;
  /** null = Unassigned Booking: the seat is assigned at boarding. */
  seatNumber: string | null;
  qrCodeUrl: string;
  /**
   * The tenant's ticket policy, its own text (plain text, newlines kept).
   * Older responses omit these; the sections are then simply not shown.
   */
  cancellationPolicyText?: string | null;
  termsAndConditionsText?: string | null;
  /** The tenant's active offices (whitelisted server-side). */
  offices?: PublicOffice[];
  trip: {
    departureTime: string;
    arrivalTime: string | null;
    originCity: string | null;
    destinationCity: string | null;
    /**
     * The PASSENGER's own boarding/alighting, pre-formatted server-side in each
     * stop city's zone by the same resolver the printed ticket uses (PR B3).
     * Null when that stop's time is genuinely unknown — never a substituted
     * clock. Older responses omit these; the pages fall back to the raw trip
     * times so a cached client keeps working.
     */
    boardingTimeFormatted?: string | null;
    alightingTimeFormatted?: string | null;
    /** Raw instants for this passenger's own leg (calendar export). */
    boardingIso?: string | null;
    alightingIso?: string | null;
    boardingAbbr?: string | null;
    alightingAbbr?: string | null;
    boardingBasis?: "passenger" | "route";
    alightingBasis?: "passenger" | "route";
    /** Instant + zone the travel DATE should be rendered from. */
    dateInstant?: string | null;
    dateTimezone?: string | null;
    /** "route" = the service's own date, not this passenger's. Must be labelled. */
    dateBasis?: "passenger" | "route";
  };
}

interface PublicOffice {
  name: string;
  cityName: string | null;
  address: string | null;
  hours: string | null;
  publicPhone: string | null;
}

/** Tenant-authored text: shown as-is (never as HTML), hidden when blank. */
function hasText(text: string | null | undefined): text is string {
  return !!text && text.trim() !== "";
}

// Status badge component
function StatusBadge({ status, t }: { status: string; t: (key: string) => string }) {
  const statusConfig: Record<string, { icon: React.ReactNode; labelKey: string; className: string }> = {
    confirmed: {
      icon: <CheckCircle2 className="h-4 w-4" />,
      labelKey: "ticket.status.confirmed",
      className: "bg-green-100 text-green-800 border-green-200",
    },
    checked_in: {
      icon: <CheckCircle2 className="h-4 w-4" />,
      labelKey: "ticket.status.checkedIn",
      className: "bg-blue-100 text-blue-800 border-blue-200",
    },
    cancelled: {
      icon: <XCircle className="h-4 w-4" />,
      labelKey: "ticket.status.cancelled",
      className: "bg-red-100 text-red-800 border-red-200",
    },
    reserved: {
      icon: <Clock className="h-4 w-4" />,
      labelKey: "ticket.status.reserved",
      className: "bg-yellow-100 text-yellow-800 border-yellow-200",
    },
    open: {
      icon: <Ticket className="h-4 w-4" />,
      labelKey: "ticket.status.open",
      className: "bg-purple-100 text-purple-800 border-purple-200",
    },
  };

  const config = statusConfig[status] || {
    icon: <QrCode className="h-4 w-4" />,
    labelKey: status,
    className: "bg-gray-100 text-gray-800 border-gray-200",
  };

  return (
    <span
      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium border ${config.className}`}
    >
      {config.icon}
      {t(config.labelKey)}
    </span>
  );
}

/**
 * The passenger's boarding office first and open; every other office is one
 * tap away (Pegasos 10-26). Matched by city name: both sides come from
 * cities.name.
 */
function orderOffices(
  offices: PublicOffice[],
  originCity: string | null,
): Array<{ office: PublicOffice; boarding: boolean }> {
  const boarding = (o: PublicOffice) =>
    !!originCity && (o.cityName ?? o.name) === originCity;
  return [
    ...offices.filter(boarding).map((office) => ({ office, boarding: true })),
    ...offices
      .filter((o) => !boarding(o))
      .map((office) => ({ office, boarding: false })),
  ];
}

export function TicketViewPage() {
  const { t } = useTranslation();
  const dl = useDateLocale();
  const params = useParams<{ ticketNumber: string }>();
  const ticketNumber = params.ticketNumber || "";
  const { slug, tenant } = useTenant();
  const [, setLocation] = useLocation();

  // Get tenant timezone
  const timezone = tenant?.timezone || DEFAULT_TIMEZONE;

  // Track page view
  useEffect(() => {
    if (slug) {
      trackPageView("ticket_view", slug);
    }
  }, [slug]);

  // Fetch ticket details
  const {
    data: ticket,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["ticket", slug, ticketNumber],
    queryFn: async () => {
      if (!slug || !ticketNumber) return null;
      return publicApi.get<TicketData>(`/api/public/${slug}/tickets/${ticketNumber}`);
    },
    enabled: !!slug && !!ticketNumber,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 1,
  });

  // The web ticket must say exactly what the PRINTED ticket says (PR B3). Both
  // now come from one server-side resolver: boarding and alighting in their own
  // stop cities' zones. This page previously rendered the trip's raw departure
  // in the tenant's zone, so the same booking showed two different times.
  const departureDate = ticket?.trip.dateInstant
    ? formatInTimezone(
        ticket.trip.dateInstant,
        ticket.trip.dateTimezone || timezone,
        dl.pattern("weekdayDateYear"),
        dl.locale,
      )
    : ticket?.trip.departureTime
      ? formatInTimezone(
          ticket.trip.departureTime,
          timezone,
          dl.pattern("weekdayDateYear"),
          dl.locale,
        )
      : "";
  // `in` rather than `??`: the server always sends this key now, and an
  // explicit null means "this passenger's boarding time is genuinely unknown".
  // Coalescing it to the route departure is exactly the substituted clock the
  // resolver refuses to produce.
  const hasResolvedTimes =
    !!ticket?.trip && "boardingTimeFormatted" in ticket.trip;
  const departureTime = hasResolvedTimes
    ? (ticket!.trip.boardingTimeFormatted ?? "—")
    : ticket?.trip.departureTime
      ? formatInTimezone(ticket.trip.departureTime, timezone, "h:mm a")
      : "";
  const departureAbbr = ticket?.trip.boardingAbbr ?? null;
  // When the boarding instant is unknown the date shown is the SERVICE's,
  // not this passenger's. The printed ticket labels that; so does this.
  const dateIsRouteBasis =
    hasResolvedTimes && ticket?.trip.dateBasis === "route";
  const arrivalTime = hasResolvedTimes
    ? (ticket!.trip.alightingTimeFormatted ?? "")
    : ticket?.trip.arrivalTime
      ? formatInTimezone(ticket.trip.arrivalTime, timezone, "h:mm a")
      : "";
  const arrivalAbbr = ticket?.trip.alightingAbbr ?? null;
  const offices = orderOffices(
    ticket?.offices ?? [],
    ticket?.trip.originCity ?? null,
  );
  const creditExpiryDate = ticket?.openTicketExpiresAt
    ? formatInTimezone(
        ticket.openTicketExpiresAt,
        timezone,
        dl.pattern("weekdayDateYear"),
        dl.locale,
      )
    : "";

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-background text-foreground flex flex-col">
        <header className="border-b bg-card">
          <div className="container mx-auto px-4 py-4">
            <div className="flex items-center justify-center">
              {tenant?.publicLogoUrl ? (
                <img
                  src={tenant.publicLogoUrl}
                  alt={tenant.name}
                  className="h-10 object-contain"
                />
              ) : (
                <h1 className="text-2xl font-bold text-primary">{tenant?.name}</h1>
              )}
            </div>
          </div>
        </header>
        <main className="container mx-auto px-4 py-12 flex-1 flex items-center justify-center">
          <div className="text-center">
            <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
            <p className="text-muted-foreground">{t("common.loading")}</p>
          </div>
        </main>
      </div>
    );
  }

  // Error/404 state
  if (error || !ticket) {
    return (
      <div className="min-h-screen bg-background text-foreground flex flex-col">
        <header className="border-b bg-card">
          <div className="container mx-auto px-4 py-4">
            <div className="flex items-center justify-center">
              {tenant?.publicLogoUrl ? (
                <img
                  src={tenant.publicLogoUrl}
                  alt={tenant.name}
                  className="h-10 object-contain"
                />
              ) : (
                <h1 className="text-2xl font-bold text-primary">{tenant?.name}</h1>
              )}
            </div>
          </div>
        </header>
        <main className="container mx-auto px-4 py-12 flex-1">
          <div className="max-w-md mx-auto text-center">
            <AlertCircle className="h-16 w-16 text-destructive mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">{t("ticket.notFound")}</h2>
            <p className="text-muted-foreground mb-6">
              {t("ticket.notFoundDescription")}
            </p>
            <Button onClick={() => setLocation("/")}>
              {t("ticket.bookTrip")}
            </Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <SEO {...SEOPresets.ticket} />
      {/* Minimal header for mobile */}
      <header className="border-b bg-card">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-center">
            {tenant?.publicLogoUrl ? (
              <img
                src={tenant.publicLogoUrl}
                alt={tenant.name}
                className="h-8 object-contain"
              />
            ) : (
              <h1 className="text-xl font-bold text-primary">{tenant?.name}</h1>
            )}
          </div>
        </div>
      </header>

      {/* Main ticket content - optimized for showing to driver */}
      <main id="main-content" className="flex-1 flex flex-col">
        {/* F2: a parked travel credit is NOT a boarding pass — show the credit
            notice instead of a (suppressed) boarding QR. */}
        {ticket.isTravelCredit ? (
          <div className="bg-white py-8 px-4 text-center">
            <div
              className="max-w-sm mx-auto rounded-lg border border-purple-200 bg-purple-50 p-6"
              data-testid="ticket-travel-credit"
            >
              <Ticket className="h-12 w-12 text-purple-700 mx-auto mb-3" />
              <h2 className="text-lg font-bold text-purple-900 mb-2">
                {t("ticket.travelCredit.title")}
              </h2>
              <p className="text-sm text-purple-900/80">
                {t("ticket.travelCredit.description")}
              </p>
              {creditExpiryDate && (
                <p className="text-sm font-semibold text-purple-900 mt-3">
                  {t("ticket.travelCredit.validUntil", { date: creditExpiryDate })}
                </p>
              )}
            </div>
          </div>
        ) : (
          /* QR Code - Large and centered for easy scanning */
          <div className="bg-white py-6 px-4 text-center">
            {ticket.qrCodeUrl ? (
              <img
                src={ticket.qrCodeUrl}
                alt="Ticket QR Code"
                className="w-56 h-56 mx-auto"
                data-testid="ticket-qr"
              />
            ) : (
              <div className="w-56 h-56 mx-auto bg-muted flex items-center justify-center rounded-lg">
                <QrCode className="h-16 w-16 text-muted-foreground" />
              </div>
            )}
          </div>
        )}

        {/* Ticket info card */}
        <div className="flex-1 bg-card px-4 py-4">
          <div className="max-w-sm mx-auto space-y-4">
            {/* Ticket number and status */}
            <div className="text-center space-y-2">
              <p
                className="font-mono text-lg font-bold tracking-wider"
                data-testid="ticket-number"
              >
                {ticket.ticketNumber}
              </p>
              <StatusBadge status={ticket.status} t={t} />
              {ticket.awaitingPayment && (
                <div data-testid="awaiting-payment" className="space-y-1">
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-semibold border bg-amber-100 text-amber-900 border-amber-300">
                    <AlertCircle className="h-4 w-4" />
                    {t("ticket.awaitingPayment")}
                  </span>
                  <p className="text-xs text-muted-foreground">
                    {t("ticket.awaitingPaymentHint")}
                  </p>
                </div>
              )}
            </div>

            <hr className="border-dashed" />

            {/* Route */}
            <div className="flex items-start gap-3">
              <MapPin className="h-5 w-5 text-primary mt-0.5 shrink-0" />
              <p className="font-semibold text-lg">
                {ticket.trip.originCity} → {ticket.trip.destinationCity}
              </p>
            </div>

            {/* Date and time */}
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-primary shrink-0" />
                <span className="font-medium">
                  {departureDate}
                  {dateIsRouteBasis && (
                    <span className="font-normal text-muted-foreground">
                      {" "}
                      {t("trips.routeDeparture", "(route departure)")}
                    </span>
                  )}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-primary shrink-0" />
                <span className="font-medium">
                  {departureTime}
                  {departureAbbr && (
                    <span className="text-muted-foreground"> {departureAbbr}</span>
                  )}
                  {arrivalTime && (
                    <span className="text-muted-foreground">
                      {" "}
                      - {arrivalTime}
                      {arrivalAbbr ? ` ${arrivalAbbr}` : ""}
                    </span>
                  )}
                </span>
              </div>
            </div>

            {/* Passenger and seat */}
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-2">
                <User className="h-5 w-5 text-primary shrink-0" />
                <span className="font-medium truncate">{ticket.passengerName}</span>
              </div>
              <div className="flex items-center gap-2">
                <Armchair className="h-5 w-5 text-primary shrink-0" />
                <span className="font-medium" data-testid="ticket-seat">
                  {ticket.seatNumber
                    ? `${t("confirmation.seat")} ${ticket.seatNumber}`
                    : t("ticket.seatAtBoarding")}
                </span>
              </div>
            </div>

            {/* Show to driver hint — only for an actual boarding pass, never a
                parked travel credit (F2). */}
            {!ticket.isTravelCredit && (
              <p className="text-center text-sm text-muted-foreground pt-2">
                {t("ticket.showToDriver")}
              </p>
            )}
          </div>
        </div>

        {/* Ticket policy + offices: the tenant's own rules and counters. */}
        {(hasText(ticket.cancellationPolicyText) ||
          hasText(ticket.termsAndConditionsText) ||
          offices.length > 0) && (
          <div className="bg-card border-t px-4 py-4">
            <div className="max-w-sm mx-auto space-y-5">
              {hasText(ticket.cancellationPolicyText) && (
                <section data-testid="ticket-policy-changes">
                  <h2 className="font-semibold mb-1">
                    {t("ticket.policy.changesTitle")}
                  </h2>
                  <p className="text-sm text-muted-foreground whitespace-pre-line">
                    {ticket.cancellationPolicyText}
                  </p>
                </section>
              )}
              {hasText(ticket.termsAndConditionsText) && (
                <section data-testid="ticket-policy-travel">
                  <h2 className="font-semibold mb-1">
                    {t("ticket.policy.travelTitle")}
                  </h2>
                  <p className="text-sm text-muted-foreground whitespace-pre-line">
                    {ticket.termsAndConditionsText}
                  </p>
                </section>
              )}
              {offices.length > 0 && (
                <section data-testid="ticket-offices">
                  <h2 className="font-semibold mb-2">
                    {t("ticket.offices.title")}
                  </h2>
                  <ul className="divide-y rounded-md border">
                    {offices.map(({ office, boarding }, i) => {
                      const hasDetails =
                        !!office.address || !!office.hours || !!office.publicPhone;
                      const heading = (
                        <span className="flex min-w-0 flex-1 items-center gap-2">
                          <Building2 className="h-4 w-4 text-primary shrink-0" />
                          <span className="min-w-0 font-medium">
                            {office.name}
                            {office.cityName && office.cityName !== office.name && (
                              <span className="font-normal text-muted-foreground">
                                {" "}
                                · {office.cityName}
                              </span>
                            )}
                            {boarding && (
                              <span className="block text-xs font-normal text-primary">
                                {t("ticket.offices.boarding")}
                              </span>
                            )}
                          </span>
                        </span>
                      );
                      return (
                        <li
                          key={`${i}-${office.name}`}
                          className="text-sm"
                          data-testid={boarding ? "ticket-office-boarding" : undefined}
                        >
                          {hasDetails ? (
                            <details open={boarding} className="group">
                              <summary className="flex cursor-pointer list-none items-center gap-2 px-3 py-2.5 [&::-webkit-details-marker]:hidden">
                                {heading}
                                <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0 transition-transform group-open:rotate-180" />
                              </summary>
                              <div className="space-y-1 px-3 pb-3 pl-9">
                                {office.address && (
                                  <p className="text-muted-foreground">{office.address}</p>
                                )}
                                {office.hours && (
                                  <p className="text-muted-foreground whitespace-pre-line">
                                    {t("ticket.offices.hours")}: {office.hours}
                                  </p>
                                )}
                                {office.publicPhone && (
                                  <a
                                    href={`tel:${office.publicPhone.replace(/[^\d+]/g, "")}`}
                                    className="inline-flex items-center gap-1 text-primary"
                                  >
                                    <Phone className="h-3.5 w-3.5" />
                                    {office.publicPhone}
                                  </a>
                                )}
                              </div>
                            </details>
                          ) : (
                            <div className="flex items-center gap-2 px-3 py-2.5">
                              {heading}
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </section>
              )}
            </div>
          </div>
        )}
      </main>

      {/* Minimal footer */}
      <footer className="border-t bg-muted/30 py-2">
        <p className="text-center text-xs text-muted-foreground">
          {t("common.poweredBy")}
        </p>
      </footer>
    </div>
  );
}

export default TicketViewPage;
